import Utils from '@/common/utils/utils';
import localStorageUtil from '@/common/utils/localStorageUtil';
import TranslationConstant from '@/common/constant/TranslationConstant';
import { translateContentOnDemand } from '@/service/translationService';
import Constant from '@/common/constant/Constant';

const {
    ON_DEMAND_TRANSLATION_STATUS,
    ON_DEMAND_TRANSLATION_VIEW,
    ON_DEMAND_SOURCE_TYPE,
    ON_DEMAND_LIST_FIELD,
    ON_DEMAND_LIST_KEY_SEPARATOR
} = TranslationConstant;

/**
 * Re-shape the BE's FLAT translation map into a per-record nested map.
 *
 * The BE translates each list field as a single plain item keyed "<ticketId>::<field>"
 * and returns one flat map { "<ticketId>::outline": "...", "<ticketId>::lastMessage": "..." }.
 * mergeListTicketTranslations expects { [ticketId]: { outline, lastMessage } }, so split
 * each key on the separator and group by ticketId.
 *
 * @param {Object|null} flatTranslations - { "<ticketId>::<field>": translatedText } or null.
 * @returns {Object|null} { [ticketId]: { outline?, lastMessage? } } or null (passed through).
 */
const reshapeListTranslations = (flatTranslations) => {
    if (flatTranslations == null) return null;

    const nested = {};
    Object.entries(flatTranslations).forEach(([key, value]) => {
        const sepIndex = key.indexOf(ON_DEMAND_LIST_KEY_SEPARATOR);
        if (sepIndex < 0) return;
        const ticketId = key.slice(0, sepIndex);
        const field = key.slice(sepIndex + ON_DEMAND_LIST_KEY_SEPARATOR.length);
        if (!ticketId || !field) return;
        if (!nested[ticketId]) nested[ticketId] = {};
        nested[ticketId][field] = value;
    });
    return nested;
};

// Panel types that have a last-message column on their list screen (§5.1).
const PANEL_TYPES_WITH_LAST_MESSAGE = [
    Constant.PANEL_TYPE.HELPDESK_TICKET_LIST,
    Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST,
];

/**
 * Whether the given panelType's list screen includes a last-message column.
 * Knowledge list does not (§5.1).
 */
const panelHasLastMessage = (panelType) =>
    PANEL_TYPES_WITH_LAST_MESSAGE.includes(panelType);

/**
 * Build a fresh per-record translation entry.
 *
 * The FE cannot tell at init time whether a ticket has a last message (that data is only
 * resolved server-side), so it does NOT track hasLastMessage. Instead the BE is asked for
 * the last message (on panels that have one) and answers whether there is one; the row
 * records that answer in lastMessageChecked so it is asked only once (prevents a loop for
 * tickets that have no last message).
 *
 * @returns {Object}
 */
const buildTicketRow = () => ({
    isTranslatable: true,
    status: ON_DEMAND_TRANSLATION_STATUS.IDLE,
    translatedOutline: null,
    translatedLastMessage: null,
    // true once the BE has answered for this record's last message (whether it had one or
    // not). Until then, a panel with a last-message column keeps the record pending so the
    // request includes 'lastMessage'. Set on merge of any request that asked for it.
    lastMessageChecked: false
});

/**
 * Whether a record still needs translating.
 *
 * A record is pending when:
 *   - title not translated yet, OR
 *   - the list has a last-message column AND we have not yet asked the BE about this
 *     record's last message (lastMessageChecked === false).
 *
 * Guards: LOADING prevents re-fire during in-flight request; ERROR parks until explicit retry.
 *
 * @param {Object} row - record from the tickets map.
 * @param {boolean} listCanHaveLastMessage - whether the panel has a last-message column.
 */
const isPendingTranslationRow = (row, listCanHaveLastMessage) => {
    if (!row || !row.isTranslatable) return false;
    if (row.status === ON_DEMAND_TRANSLATION_STATUS.LOADING) return false;
    if (row.status === ON_DEMAND_TRANSLATION_STATUS.ERROR) return false;

    // Title not translated yet → pending.
    if (row.translatedOutline == null) return true;

    // Title done, but the BE has not yet told us about the last message → pending.
    if (listCanHaveLastMessage && !row.lastMessageChecked) return true;

    return false;
};

/**
 * Build the translateFields array for a single pending record, telling the BE exactly
 * which fields still need resolving. The BE decides whether a last message actually
 * exists; the FE only requests it (on last-message panels) until it has been checked.
 *
 * @param {Object} row - record from the tickets map.
 * @param {boolean} listCanHaveLastMessage - whether the panel has a last-message column.
 * @returns {string[]} e.g. ['outline'] or ['outline', 'lastMessage'] or ['lastMessage']
 */
const buildTranslateFields = (row, listCanHaveLastMessage) => {
    const fields = [];
    if (row.translatedOutline == null) fields.push(ON_DEMAND_LIST_FIELD.OUTLINE);
    if (listCanHaveLastMessage && !row.lastMessageChecked) fields.push(ON_DEMAND_LIST_FIELD.LAST_MESSAGE);
    return fields;
};

/**
 * Shallow equality for a list record, comparing only the fields init can produce.
 */
const isSameListRecord = (a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;

    if (a.toggleStatus !== b.toggleStatus || a.activeView !== b.activeView) {
        return false;
    }

    const aTickets = a.tickets || {};
    const bTickets = b.tickets || {};
    if (aTickets === bTickets) return true;

    const aKeys = Object.keys(aTickets);
    const bKeys = Object.keys(bTickets);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
        const ar = aTickets[key];
        const br = bTickets[key];
        if (!br) return false;
        if (
            ar.isTranslatable !== br.isTranslatable ||
            ar.status !== br.status ||
            ar.translatedOutline !== br.translatedOutline ||
            ar.translatedLastMessage !== br.translatedLastMessage ||
            ar.lastMessageChecked !== br.lastMessageChecked
        ) {
            return false;
        }
    }
    return true;
};

/**
 * Initialize / extend the on-demand translation state for a ticket list.
 *
 * New ids get a fresh record; ids already present are kept untouched (translations and
 * the lastMessageChecked flag survive, BR-6 / BR-8). The FE does NOT inspect the item for
 * a last message — only the record id matters here; the BE resolves the last message when
 * translating and reports back whether one exists.
 *
 * @param {Function} setListTicketOnDemand - Recoil setter for the per-screen list atom.
 * @param {string} panelType - Constant.PANEL_TYPE.* (decides last-message column).
 * @param {string} viewKey - current view key (enum for QA, viewId for helpdesk/knowledge).
 * @param {Array} items - the current list records (ticket / knowledge); only item.id is read.
 */
export const initListTicketOnDemand = (setListTicketOnDemand, panelType, viewKey, items) => {
    if (typeof setListTicketOnDemand !== 'function' || !panelType || !viewKey) {
        return;
    }

    setListTicketOnDemand((prev) => {
        const record = prev[viewKey] || {
            toggleStatus: ON_DEMAND_TRANSLATION_STATUS.IDLE,
            activeView: ON_DEMAND_TRANSLATION_VIEW.ORIGINAL,
            tickets: {}
        };

        const nextTickets = { ...record.tickets };
        (items || []).forEach((item) => {
            const ticketId = item?.id;
            if (!ticketId) return;
            // Keep any record already present (translated / loading / idle): never overwrite
            // a cached translation or reset an in-flight one.
            if (!nextTickets[ticketId]) {
                nextTickets[ticketId] = buildTicketRow();
            }
        });

        const nextRecord = { ...record, tickets: nextTickets };
        return isSameListRecord(record, nextRecord)
            ? prev
            : { ...prev, [viewKey]: nextRecord };
    });
};

/**
 * Update the whole-list toggle status for a view.
 */
export const setListTicketOnDemandToggleStatus = (setListTicketOnDemand, viewKey, toggleStatus) => {
    if (typeof setListTicketOnDemand !== 'function' || !viewKey) return;

    setListTicketOnDemand((prev) => {
        const record = prev[viewKey];
        if (!record || record.toggleStatus === toggleStatus) return prev;
        return { ...prev, [viewKey]: { ...record, toggleStatus } };
    });
};

/**
 * Update the view-level active view (ON = translation, OFF = original).
 */
export const setListTicketOnDemandActiveView = (setListTicketOnDemand, viewKey, activeView) => {
    if (typeof setListTicketOnDemand !== 'function' || !viewKey) return;

    setListTicketOnDemand((prev) => {
        const record = prev[viewKey];
        if (!record || record.activeView === activeView) return prev;
        return { ...prev, [viewKey]: { ...record, activeView } };
    });
};

/**
 * Collect the records that still need translating, including which fields each one needs.
 *
 * @param {Object} listRecord - a list record ({ tickets }).
 * @param {string} panelType - Constant.PANEL_TYPE.* (decides whether lastMessage is requested).
 * @returns {Array<{ ticketId: string, translateFields: string[] }>}
 */
const getRowsNeedTranslate = (listRecord, panelType) => {
    const listCanHaveLastMessage = panelHasLastMessage(panelType);
    return Object.entries(listRecord?.tickets || {})
        .filter(([, row]) => isPendingTranslationRow(row, listCanHaveLastMessage))
        .map(([ticketId, row]) => ({
            ticketId,
            translateFields: buildTranslateFields(row, listCanHaveLastMessage)
        }));
};

/**
 * Mark the view LOADING and set each sent record to LOADING in one atom update.
 *
 * @param {Function} setListTicketOnDemand - Recoil setter.
 * @param {string} viewKey - view key.
 * @param {string[]} ticketIds - ids of records being sent (extracted from the tickets payload).
 */
const markRowsLoading = (setListTicketOnDemand, viewKey, ticketIds) => {
    if (!viewKey) return;
    setListTicketOnDemand((prev) => {
        const record = prev[viewKey];
        if (!record) return prev;

        const idSet = new Set(ticketIds);
        const nextTickets = Object.fromEntries(
            Object.entries(record.tickets || {}).map(([id, row]) =>
                idSet.has(id)
                    ? [id, { ...row, status: ON_DEMAND_TRANSLATION_STATUS.LOADING }]
                    : [id, row]
            )
        );

        return {
            ...prev,
            [viewKey]: {
                ...record,
                toggleStatus: ON_DEMAND_TRANSLATION_STATUS.LOADING,
                tickets: nextTickets
            }
        };
    });
};

/**
 * Translate the pending records of a list on demand, then merge the results.
 *
 * Payload sent to BE:
 *   {
 *     sourceType: 'LIST_TICKET',
 *     panelType,
 *     tickets: [
 *       { ticketId: '...', translateFields: ['outline', 'lastMessage'] },
 *       { ticketId: '...', translateFields: ['outline'] },
 *       { ticketId: '...', translateFields: ['lastMessage'] },
 *       ...
 *     ]
 *   }
 *
 * BE returns (via S3): { [ticketId]: { outline?, lastMessage? } }
 */
export const requestListTicketOnDemandTranslation = async ({
    panelType,
    viewKey,
    listRecord,
    setListTicketOnDemand,
    setIsOpenModalReloadPage
}) => {
    if (typeof setListTicketOnDemand !== 'function' || !panelType || !viewKey) return;

    const pendingRows = getRowsNeedTranslate(listRecord, panelType);

    // Nothing to translate → just show cached translations.
    if (Utils.isEmptyArray(pendingRows)) {
        setListTicketOnDemandActiveView(
            setListTicketOnDemand, viewKey, ON_DEMAND_TRANSLATION_VIEW.TRANSLATION
        );
        setListTicketOnDemandToggleStatus(
            setListTicketOnDemand, viewKey, ON_DEMAND_TRANSLATION_STATUS.TRANSLATED
        );
        return;
    }

    const ticketIds = pendingRows.map(r => r.ticketId);
    // Ids that asked for the last message in this request: on merge they get
    // lastMessageChecked = true even if the BE returns no last message, so a ticket
    // without one is asked only once (no auto-translate loop).
    const lastMessageRequestedIds = pendingRows
        .filter(r => r.translateFields.includes(ON_DEMAND_LIST_FIELD.LAST_MESSAGE))
        .map(r => r.ticketId);

    // Mark LOADING before awaiting to prevent auto-translate re-fire.
    markRowsLoading(setListTicketOnDemand, viewKey, ticketIds);

    const affiliationOrganization = localStorageUtil.getAffiliationOrganization();
    const userId = localStorageUtil.getUserDetailId();

    // viewKey is a FE-only partition key; the BE only needs panelType + tickets.
    const translateArgs = {
        sourceType: ON_DEMAND_SOURCE_TYPE.LIST_TICKET,
        panelType,
        tickets: pendingRows.map(({ ticketId, translateFields }) => ({
            ticketId,
            translateFields
        }))
    };

    const { translations, errorCode } = await translateContentOnDemand(
        affiliationOrganization, userId, translateArgs
    );

    if (errorCode) {
        if (typeof setIsOpenModalReloadPage === 'function') {
            setIsOpenModalReloadPage(true);
        }
        mergeListTicketTranslations(setListTicketOnDemand, viewKey, ticketIds, null, lastMessageRequestedIds);
        return;
    }

    // BE returns a FLAT map keyed "<ticketId>::<field>"; re-shape to { [ticketId]: { outline, lastMessage } }.
    mergeListTicketTranslations(
        setListTicketOnDemand,
        viewKey,
        ticketIds,
        reshapeListTranslations(translations),
        lastMessageRequestedIds
    );
};

/**
 * Merge the BE translation result into the list record and flip it ON.
 *
 * Uses nullish coalescing to keep already-cached fields: if the BE only translated
 * lastMessage (because outline was already cached), `result.outline` is undefined →
 * `row.translatedOutline` stays. Same for the reverse.
 *
 * Any id that requested the last message gets lastMessageChecked = true here, whether or
 * not the BE returned one — a ticket with no last message is therefore asked only once
 * and never re-enters the pending set (no auto-translate loop). On total failure the flag
 * is NOT set, so a failed request can be retried.
 *
 * @param {string[]} [lastMessageRequestedIds] - ids whose request included 'lastMessage'.
 */
export const mergeListTicketTranslations = (
    setListTicketOnDemand,
    viewKey,
    requestedIds,
    translations,
    lastMessageRequestedIds = []
) => {
    if (typeof setListTicketOnDemand !== 'function' || !viewKey) return;

    setListTicketOnDemand((prev) => {
        const record = prev[viewKey];
        if (!record) return prev;

        const requested = new Set(requestedIds);
        const lastMessageAsked = new Set(lastMessageRequestedIds);
        const failedAll = translations == null;

        const nextTickets = Object.fromEntries(
            Object.entries(record.tickets || {}).map(([id, row]) => {
                if (!requested.has(id)) return [id, row];

                // Total failure (translations == null) → ERROR, keep original, allow retry.
                if (failedAll) {
                    return [id, { ...row, status: ON_DEMAND_TRANSLATION_STATUS.ERROR }];
                }

                // Partial success for this id: result may be undefined when the id had only a
                // lastMessage request and the BE found no last message (so it returned no key).
                // That is NOT an error — mark it TRANSLATED and checked so it is not retried.
                const result = translations[id] || {};
                return [id, {
                    ...row,
                    status: ON_DEMAND_TRANSLATION_STATUS.TRANSLATED,
                    translatedOutline: result.outline ?? row.translatedOutline ?? null,
                    translatedLastMessage: result.lastMessage ?? row.translatedLastMessage ?? null,
                    // Asked for last message this request → mark checked (even if the BE
                    // returned none), so a ticket without a last message is asked only once.
                    lastMessageChecked: row.lastMessageChecked || lastMessageAsked.has(id)
                }];
            })
        );

        return {
            ...prev,
            [viewKey]: {
                ...record,
                toggleStatus: failedAll
                    ? ON_DEMAND_TRANSLATION_STATUS.ERROR
                    : ON_DEMAND_TRANSLATION_STATUS.TRANSLATED,
                activeView: failedAll
                    ? record.activeView
                    : ON_DEMAND_TRANSLATION_VIEW.TRANSLATION,
                tickets: nextTickets
            }
        };
    });
};

/**
 * Auto-translate records that appeared while a list is already ON (§4.3 / BR-3a).
 * Also picks up records that gained a last message after initial translation.
 */
export const autoTranslateNewListTickets = ({
    panelType,
    viewKey,
    listRecord,
    setListTicketOnDemand,
    setIsOpenModalReloadPage
}) => {
    if (typeof setListTicketOnDemand !== 'function' || !panelType || !viewKey || !listRecord) return;
    if (listRecord.activeView !== ON_DEMAND_TRANSLATION_VIEW.TRANSLATION) return;
    if (listRecord.toggleStatus === ON_DEMAND_TRANSLATION_STATUS.LOADING) return;
    if (Utils.isEmptyArray(getRowsNeedTranslate(listRecord, panelType))) return;

    requestListTicketOnDemandTranslation({
        panelType, viewKey, listRecord, setListTicketOnDemand, setIsOpenModalReloadPage
    });
};

/**
 * Whether the list should currently show translations for a given record.
 */
const isRowShowingTranslation = (listRecord, ticketId) =>
    !!listRecord?.tickets?.[ticketId] &&
    listRecord.activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION;

/**
 * Resolve the translated TITLE for a list row, or null to fall back to the original.
 */
export const getListRowTranslatedOutline = (listRecord, ticketId) => {
    if (!isRowShowingTranslation(listRecord, ticketId)) return null;
    const translated = listRecord.tickets[ticketId].translatedOutline;
    return Utils.isEmptyString(translated) ? null : translated;
};

/**
 * Resolve the translated LAST MESSAGE for a list row, or null to fall back to the original.
 */
export const getListRowTranslatedLastMessage = (listRecord, ticketId) => {
    if (!isRowShowingTranslation(listRecord, ticketId)) return null;
    const translated = listRecord.tickets[ticketId].translatedLastMessage;
    return Utils.isEmptyString(translated) ? null : translated;
};
